import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Alert, Badge, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { SvgTemplate } from "../types/templates";

type SVGSelectorProps = {
  templates: SvgTemplate[];
  setSvgContent: React.Dispatch<React.SetStateAction<string | null>>;
  isLoading?: boolean;
  error?: string | null;
  onRefreshTemplates?: () => void;
  lastFetchedAt?: Date | null;
};

const DEFAULT_CATEGORIES: Array<{ label: string; icon: string }> = [
  { label: "All", icon: "fluent-color:cloud-16" },
  { label: "AWS", icon: "devicon:amazonwebservices-wordmark" },
  { label: "Azure", icon: "devicon:azure" },
  { label: "GCP", icon: "logos:google-cloud" },
  { label: "DevOps", icon: "devicon:azuredevops" },
];

const SVGSelector: React.FC<SVGSelectorProps> = ({
  templates,
  setSvgContent,
  isLoading = false,
  error,
  onRefreshTemplates,
  lastFetchedAt,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewSvg, setPreviewSvg] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(templates.map((template) => template.category)));
    const filteredDefaults = DEFAULT_CATEGORIES.filter(
      (category) => category.label === "All" || uniqueCategories.includes(category.label)
    );

    const missing = uniqueCategories.filter(
      (category) => !filteredDefaults.some((defaultCategory) => defaultCategory.label === category)
    );

    const supplemental = missing.map((category) => ({
      label: category,
      icon: "solar:folder-line-duotone",
    }));

    return [...filteredDefaults, ...supplemental];
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return templates
      .filter((template) => selectedCategory === "All" || template.category === selectedCategory)
      .filter((template) => {
        if (!normalizedQuery) {
          return true;
        }
        const description = template.metadata?.description?.toLowerCase() ?? "";
        const provider = template.metadata?.provider?.toLowerCase() ?? "";
        const tags = template.metadata?.tags?.map((tag) => tag.toLowerCase()) ?? [];
        return (
          template.name.toLowerCase().includes(normalizedQuery) ||
          description.includes(normalizedQuery) ||
          provider.includes(normalizedQuery) ||
          tags.some((tag) => tag.includes(normalizedQuery))
        );
      });
  }, [templates, selectedCategory, searchQuery]);

  const handleUseSVG = async (path: string) => {
    setSelectionError(null);
    try {
      const response = await fetch(path);
      if (!response.ok) {
        throw new Error(`Unable to download template (${response.statusText}).`);
      }
      const svgContent = await response.text();
      setSvgContent(svgContent);
      setIsModalOpen(false);
    } catch (downloadError: unknown) {
      const message =
        downloadError instanceof Error ? downloadError.message : "Unable to download the selected SVG template.";
      setSelectionError(message);
    }
  };

  const handlePreviewSVG = (path: string) => {
    setPreviewSvg(path);
  };

  const handleRefresh = () => {
    if (onRefreshTemplates) {
      onRefreshTemplates();
    }
  };

  return (
    <div className="template-library">
      <button className="btn btn-primary mb-3" onClick={() => setIsModalOpen(true)}>
        Choose Template
      </button>

      {isModalOpen && (
        <div className="modal show d-block" tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select an SVG Template</h5>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close"></button>
              </div>

              <div className="modal-body">
                <div className="d-flex flex-column gap-3 mb-4">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <InputGroup className="template-search">
                      <InputGroup.Text id="template-search-icon">
                        <Icon icon="solar:magnifer-linear" width={18} height={18} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search by name, provider, or tag"
                        aria-label="Search templates"
                        aria-describedby="template-search-icon"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                      />
                      {onRefreshTemplates && (
                        <Button
                          variant="outline-secondary"
                          onClick={handleRefresh}
                          disabled={isLoading}
                          aria-label="Refresh template catalog"
                        >
                          <Icon icon="solar:refresh-outline" width={18} height={18} className="me-1" />
                          Refresh
                        </Button>
                      )}
                    </InputGroup>

                    <div className="d-flex align-items-center gap-2 text-muted small">
                      {isLoading && (
                        <span className="d-inline-flex align-items-center gap-1">
                          <Spinner size="sm" animation="border" role="status" />
                          Syncing catalog…
                        </span>
                      )}
                      {lastFetchedAt && !isLoading && (
                        <span>
                          Updated {lastFetchedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {categories.map((category) => (
                      <button
                        key={category.label}
                        className={`btn btn-outline-secondary ${
                          selectedCategory === category.label ? "active" : ""
                        }`}
                        onClick={() => setSelectedCategory(category.label)}
                      >
                        <Icon icon={category.icon} className="me-1" /> {category.label}
                      </button>
                    ))}
                  </div>
                </div>

                {error && <Alert variant="warning">{error}</Alert>}
                {selectionError && <Alert variant="danger">{selectionError}</Alert>}

                <div className="row template-grid">
                  {filteredTemplates.map((template) => (
                    <div key={template.id ?? template.path ?? template.name} className="col-lg-4 col-md-6 mb-4">
                      <div className="card h-100 shadow-sm template-card">
                        <div className="template-card-preview" role="presentation">
                          <img
                            src={template.path}
                            alt={template.name}
                            className="card-img-top p-2"
                            style={{ maxHeight: "200px", objectFit: "contain" }}
                          />
                        </div>
                        <div className="card-body text-center">
                          <h5 className="card-title mb-2">{template.name}</h5>
                          <div className="d-flex flex-wrap justify-content-center gap-2 mb-2">
                            <Badge bg="secondary">{template.category}</Badge>
                            {template.source && <Badge bg="info">{template.source.toUpperCase()}</Badge>}
                            {template.metadata?.provider && <Badge bg="light" text="dark">{template.metadata.provider}</Badge>}
                          </div>
                          <p className="card-text text-muted small mb-0">
                            {template.metadata?.description ?? "No description provided."}
                          </p>
                        </div>
                        {template.metadata?.tags && template.metadata.tags.length > 0 && (
                          <div className="card-body pt-0 text-center">
                            <div className="d-flex flex-wrap justify-content-center gap-1">
                              {template.metadata.tags.map((tag) => (
                                <Badge
                                  bg="light"
                                  text="dark"
                                  key={`${template.name}-${tag}`}
                                  className="template-tag"
                                >
                                  #{tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="card-footer d-flex justify-content-between p-2">
                          <Button variant="outline-info" onClick={() => handlePreviewSVG(template.path)}>
                            <Icon icon="solar:eye-broken" className="me-1" /> Preview
                          </Button>
                          <Button variant="success" onClick={() => handleUseSVG(template.path)}>
                            <Icon icon="solar:play-linear" className="me-1" /> Use
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {!isLoading && filteredTemplates.length === 0 && (
                    <div className="col-12">
                      <div className="empty-state text-center p-5 border rounded-3">
                        <Icon icon="solar:clipboard-search-outline" width={48} height={48} className="mb-3 text-muted" />
                        <h5>No templates found</h5>
                        <p className="text-muted">
                          Try a different search term or pick another category. Refresh the catalog to sync new diagrams.
                        </p>
                        {onRefreshTemplates && (
                          <Button variant="outline-secondary" onClick={handleRefresh} disabled={isLoading}>
                            <Icon icon="solar:refresh-outline" className="me-1" /> Refresh catalog
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewSvg && (
        <div className="modal show d-block" tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">SVG Preview</h5>
                <button type="button" className="btn-close" onClick={() => setPreviewSvg(null)}></button>
              </div>
              <div className="modal-body text-center">
                <img src={previewSvg} alt="SVG Preview" className="img-fluid" />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewSvg(null)}>
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SVGSelector;
